import React, { useLayoutEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  estimateOpenColumnOrderRowSize,
  shouldVirtualizeOpenColumnCityOrders,
  OPEN_COLUMN_CITY_ORDERS_VIRTUAL_ENABLED,
} from '../utils/openColumnCityVirtual';
import { remeasureMountedVirtualItems } from '../utils/remeasureVirtualItems';
import './OpenColumnVirtualOrdersList.css';

/**
 * Virtualiza .order-row dentro de .city-orders-container (cidade grande).
 * O próprio container (max-height) é o scrollElement.
 */
export function OpenColumnVirtualOrdersList({
  orders = [],
  enabled = OPEN_COLUMN_CITY_ORDERS_VIRTUAL_ENABLED,
  overscan = 6,
  children,
}) {
  const list = Array.isArray(orders) ? orders : [];
  const parentRef = useRef(null);
  const virtualHostRef = useRef(null);
  const useVirtual = enabled && shouldVirtualizeOpenColumnCityOrders(list.length);

  const rowVirtualizer = useVirtualizer({
    count: useVirtual ? list.length : 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateOpenColumnOrderRowSize(),
    overscan,
    measureElement:
      typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
        ? (el) => el?.getBoundingClientRect().height
        : undefined,
  });

  useLayoutEffect(() => {
    if (!useVirtual) return;
    // Re-medir os itens montados; measure() limparia o cache e devolveria as
    // rows para estimateSize (sobra de espaço no fim do card da cidade).
    remeasureMountedVirtualItems(virtualHostRef.current, rowVirtualizer);
  }, [useVirtual, list.length, rowVirtualizer]);

  if (!useVirtual) {
    return (
      <div className="city-orders-container" ref={parentRef}>
        <div className="orders-table">
          {list.map((ordem, index) => (
            <React.Fragment key={ordem?.id ?? `os-${index}`}>
              {typeof children === 'function' ? children(ordem, index) : null}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  const virtualItems = rowVirtualizer.getVirtualItems();
  if (list.length > 0 && virtualItems.length === 0) {
    return (
      <div className="city-orders-container" ref={parentRef} data-virtual-fallback="1">
        <div className="orders-table">
          {list.map((ordem, index) => (
            <React.Fragment key={ordem?.id ?? `os-${index}`}>
              {typeof children === 'function' ? children(ordem, index) : null}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="city-orders-container city-orders-container--virtual" ref={parentRef}>
      <div
        className="orders-table orders-table--virtual"
        ref={virtualHostRef}
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualItems.map((virtualRow) => {
          const ordem = list[virtualRow.index];
          return (
            <div
              key={ordem?.id ?? virtualRow.key}
              className="open-column-virtual-order-item"
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {typeof children === 'function' ? children(ordem, virtualRow.index) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default OpenColumnVirtualOrdersList;
